<script setup lang="ts">
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import DropdownSensors from '@/components/Globals/Inputs/DropdownSensors.vue'
import WeekSelector from '@/components/Globals/Inputs/WeekSelector.vue'
import SearchBarField from '@/components/Globals/SearchBarField.vue'
import IcAnimateSpin from '@/components/Icons/IcAnimateSpin.vue'
import IcSearch from '@/components/Icons/IcSearch.vue'
import { Form } from 'vee-validate'
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAnomalyDetectorStore } from '@/store/anomaly_detector'
import { useSensorStore } from '@/store/sensor'

const { t } = useI18n()
const store = useAnomalyDetectorStore()
const sensorStore = useSensorStore()

const isLoading = ref(false)
const formRef = ref<InstanceType<typeof Form>>()
const dropdownResetKey = ref(0)
const weekResetKey = ref(0)

const sensorsOptions = computed(() => sensorStore.listSensors)

const anomalyTypeOptions = ref([
	{ id: 'critical', name: 'Crítico' },
	{ id: 'attention', name: 'Atenção' },
	{ id: 'normal', name: 'Normal' },
])

const clearFilters = () => {
	formRef.value?.resetForm()
	dropdownResetKey.value += 1
	weekResetKey.value += 1
    store.setCurrentSensorFilter(null)
    store.setCurrentVariabilityLevelFilter('')
    store.setCurrentWeekFilter(null)
    store.resetSearchResults()
}

const onSubmit = async () => {
    isLoading.value = true
    try {
        store.tablePage = 1
        await store.getAnomalyDetectorPages(1, store.tableItemsPerPage)
    } finally {
        isLoading.value = false
    }
}

function onSensorSelected(sensor: any) {
    store.setCurrentSensorFilter(sensor?.id ? sensor : null)
}

function onVariabilityLevelSelected(level: any) {
    store.setCurrentVariabilityLevelFilter(level?.id || '')
}

function onWeekSelected(week: Date) {
    store.setCurrentWeekFilter(week)
}

onMounted(async () => {
    await sensorStore.fetch()
})
</script>

<template>
	<div class="mt-1 bg-white p-4 shadow-md">
		<Form ref="formRef" @submit="onSubmit" class="flex w-full flex-col gap-4">
			<div class="flex flex-wrap gap-3 items-end">
				<SearchBarField :label="t('anomaly_detector.body.table.columns.sensor')" class="min-w-[200px] flex-1">
					<DropdownSensors
						:key="`sensor-${dropdownResetKey}`"
						name="sensor"
						:options="sensorsOptions"
						:label-attribute="'name'"
						:has-all-option="true"
                        @option-selected="onSensorSelected"
						class="w-full" />
				</SearchBarField>

				<SearchBarField label="Nível de Variabilidade" class="min-w-[200px] flex-1">
					<Dropdown
						:key="`anomaly-${dropdownResetKey}`"
						name="anomaly_type"
						:options="anomalyTypeOptions"
						:label-attribute="'name'"
						:has-all-option="true"
                        @option-selected="onVariabilityLevelSelected"
						class="w-full" />
				</SearchBarField>

				<SearchBarField :label="t('anomaly_detector.body.table.columns.week')" class="min-w-[200px] flex-1">
					<WeekSelector
						:key="`week-${weekResetKey}`"
						@week-selected="onWeekSelected"
					/>
				</SearchBarField>

                <button :disabled="isLoading" class="flex flex-row items-center justify-center bg-blue h-12 px-4 py-1 text-sm text-white hover:bg-blue-2">
                <IcSearch v-if="!isLoading" class="mr-1 h-4 w-4" />
                <span v-else class="mr-1 animate-spin">
                    <IcAnimateSpin class="h-4 w-4 text-white" />
                </span>
                    Aplicar
				</button>

                <button
                :disabled="isLoading"
                type="button"
                @click="clearFilters"
                class="px-4 py-3 h-12 text-sm text-gray-700 bg-gray-200 hover:bg-gray-300">
                Limpar
                </button>				
			</div>
		</Form>
	</div>
</template>
